import BackButton from "../shared/BackButton";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { FinalResults } from "../../interfaces/ICompetitions";
import { prepareFinalResultsForDisplay, isRowDidNotPerformAllJudges } from "../../utils/roundHelpers";
import { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

interface FinalResultsTableMobileProps {
  competitionName: string;
  categoryName: string;
  finalResults: FinalResults;
  onBack?: () => void;
}

export default function FinalResultsTableMobile({
  competitionName,
  categoryName,
  finalResults,
  onBack,
}: FinalResultsTableMobileProps) {
  const sortedFinalResults = useMemo(() => prepareFinalResultsForDisplay(finalResults), [finalResults]);
  const visibleRoundColumnsCount = sortedFinalResults.rounds.length <= 1 ? 1 : 2;
  const [visibleRoundStartIndex, setVisibleRoundStartIndex] = useState(0);

  const maxVisibleRoundStartIndex = Math.max(
    0,
    sortedFinalResults.rounds.length - visibleRoundColumnsCount,
  );

  const visibleRounds = useMemo(
    () =>
      sortedFinalResults.rounds.slice(
        visibleRoundStartIndex,
        visibleRoundStartIndex + visibleRoundColumnsCount,
      ),
    [sortedFinalResults.rounds, visibleRoundColumnsCount, visibleRoundStartIndex],
  );

  const getShortJudgeName = (firstName?: string, lastName?: string) => {
    const shortFirstName = firstName?.trim()?.[0];

    if (!shortFirstName) {
      return lastName || "";
    }

    return `${shortFirstName}. ${lastName || ""}`.trim();
  };

  return (
    <Paper>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ color: "primary.main", fontSize: "1rem", fontWeight: 600 }}
          >
            Wyniki końcowe
          </Typography>
          {onBack && <BackButton onClick={onBack} showTooltip={false} />}
        </Box>
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="subtitle2" component="div" color="text.secondary">
            {categoryName}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary">
            {competitionName}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {sortedFinalResults.judges.map((judge) => (
            <Chip
              key={judge.id}
              avatar={
                <Avatar sx={{ width: 22, height: 22, fontSize: "0.7rem" }}>
                  {judge.index || `${judge.firstName?.[0] || ""}${judge.lastName?.[0] || ""}`}
                </Avatar>
              }
              label={getShortJudgeName(judge.firstName, judge.lastName)}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      </Box>

      {sortedFinalResults.rounds.length > visibleRoundColumnsCount && (
        <Box
          sx={{
            px: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Rundy {visibleRoundStartIndex + 1}-
            {Math.min(
              visibleRoundStartIndex + visibleRoundColumnsCount,
              sortedFinalResults.rounds.length,
            )}{" "}
            z {sortedFinalResults.rounds.length}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <IconButton
              size="medium"
              onClick={() =>
                setVisibleRoundStartIndex((currentIndex) => Math.max(0, currentIndex - 1))
              }
              disabled={visibleRoundStartIndex === 0}
              sx={{ p: 1 }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() =>
                setVisibleRoundStartIndex((currentIndex) =>
                  Math.min(maxVisibleRoundStartIndex, currentIndex + 1),
                )
              }
              disabled={visibleRoundStartIndex >= maxVisibleRoundStartIndex}
              sx={{ p: 1 }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      )}

      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  width: 44,
                  px: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                Miejsce
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.72rem", minWidth: 132, px: 0.75 }}>
                Nr startowy / Nazwa
              </TableCell>
              {visibleRounds.map((round) => (
                <TableCell
                  key={round.roundId}
                  align="center"
                  sx={{ fontWeight: 600, fontSize: "0.72rem", width: 72, px: 0.75 }}
                >
                  {round.name}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={2}
                align="right"
                sx={{ fontWeight: 600, fontSize: "0.68rem", pr: 1, py: 0.5 }}
              >
                Skład sędziowski
              </TableCell>
              {visibleRounds.map((round) => (
                <TableCell
                  key={`${round.roundId}-judges`}
                  align="center"
                  sx={{ fontSize: "0.68rem", px: 0.5, py: 0.5 }}
                >
                  {round.judges.join(" ")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFinalResults.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2 + visibleRounds.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Brak wyników końcowych
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedFinalResults.rows.map((row) => {
                const didNotPerform = isRowDidNotPerformAllJudges(row, sortedFinalResults.rounds);
                const strikeSx = didNotPerform
                  ? { textDecoration: "line-through", color: "text.secondary" }
                  : undefined;
                return (
                  <TableRow key={row.presentationId}>
                    <TableCell align="center" sx={{ px: 0.5, py: 0.75, fontSize: "0.75rem" }}>
                      {didNotPerform ? "\u2014" : row.place}
                    </TableCell>
                    <TableCell sx={{ py: 0.75, px: 0.75 }}>
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          color: didNotPerform ? "text.secondary" : "primary.main",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          lineHeight: 1.15,
                          ...strikeSx,
                        }}
                      >
                        {row.presentationId}
                      </Typography>
                      <Box component="div" sx={{ lineHeight: 1.15 }}>
                        <Typography variant="body2" component="span" sx={{ fontSize: "0.78rem", ...strikeSx }}>
                          {row.name}
                        </Typography>
                        {didNotPerform && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic", fontSize: "0.68rem", ml: 0.5 }}
                          >
                            (nie wystąpił)
                          </Typography>
                        )}
                      </Box>
                      {row.title && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontStyle: "italic",
                            fontSize: "0.68rem",
                            lineHeight: 1.1,
                            display: "block",
                            ...(didNotPerform ? strikeSx : {}),
                          }}
                        >
                          {row.title}
                        </Typography>
                      )}
                    </TableCell>
                    {row.rounds
                      .slice(
                        visibleRoundStartIndex,
                        visibleRoundStartIndex + visibleRoundColumnsCount,
                      )
                      .map((roundResult, index) => (
                        <TableCell
                          key={`${row.presentationId}-${visibleRoundStartIndex + index}`}
                          align="center"
                          sx={{ px: 0.75, py: 0.75, fontSize: "0.72rem", lineHeight: 1.1, width: 72 }}
                        >
                          {roundResult}
                        </TableCell>
                      ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
